import type { paths } from './schema'

/** Sesiunea e aceeasi ca in aplicatia veche: token JWT in localStorage.auth_token.
 *  Cine e logat la / e logat si la /app, fara al doilea login. */
export const token = {
  get: () => localStorage.getItem('auth_token'),
  clear: () => localStorage.removeItem('auth_token'),
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

const BASE = import.meta.env.DEV ? '/api' : `${location.origin}/api`

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const t = token.get()
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
      ...(init?.headers ?? {}),
    },
  })
  if (res.status === 401) {
    token.clear()
    location.href = '/login.html'
    throw new ApiError(401, 'Sesiune expirata')
  }
  if (!res.ok) {
    let detail = `${res.status}`
    try {
      const body = (await res.json()) as { detail?: string }
      if (body.detail) detail = body.detail
    } catch {
      /* raspuns fara corp JSON */
    }
    throw new ApiError(res.status, detail)
  }
  return (await res.json()) as T
}

type Query = Record<string, string | number | boolean | null | undefined>

function qs(params: Query) {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') s.set(k, String(v))
  }
  const out = s.toString()
  return out ? `?${out}` : ''
}

export const api = {
  get: <T>(path: string, params: Query = {}) => request<T>(path + qs(params)),
  /** PDF-urile cer acelasi token ca restul API-ului, deci nu se pot deschide cu un link
   *  simplu (ar pleca fara antet si ar primi 401). Se aduc ca blob si se deschid local. */
  deschideFisier: async (path: string) => {
    const t = token.get()
    const res = await fetch(BASE + path, { headers: t ? { Authorization: `Bearer ${t}` } : {} })
    if (!res.ok) throw new ApiError(res.status, `Nu am putut genera documentul (${res.status})`)
    const url = URL.createObjectURL(await res.blob())
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Incarcarea de fisiere (sigla, semnatura, CSV-urile de import) trimite multipart, deci
   *  NU poate merge prin `request`: acolo antetul Content-Type e fixat pe JSON, iar browserul
   *  trebuie sa-si puna singur granita (boundary) formularului. */
  incarca: async <T>(path: string, fisier: File, camp = 'file', campuri?: Record<string, string>): Promise<T> => {
    const t = token.get()
    const fd = new FormData()
    fd.append(camp, fisier)
    for (const [k, v] of Object.entries(campuri ?? {})) fd.append(k, v)
    const res = await fetch(BASE + path, {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    })
    if (res.status === 401) {
      token.clear()
      location.href = '/login.html'
      throw new ApiError(401, 'Sesiune expirata')
    }
    if (!res.ok) {
      let detail = `${res.status}`
      try {
        const body = (await res.json()) as { detail?: string }
        if (body.detail) detail = body.detail
      } catch {
        /* raspuns fara corp JSON */
      }
      throw new ApiError(res.status, detail)
    }
    return (await res.json()) as T
  },
}

/** Tipurile vin din OpenAPI (src/api/schema.d.ts, regenerat cu `npm run api:types`).
 *  Un camp redenumit in backend rupe compilarea aici, nu ecranul clientului. */
export type InvoicesPage =
  paths['/api/invoices/page']['get']['responses'][200]['content']['application/json']
